// The co-development application: who can apply, what they can help with,
// and what a valid application looks like. Shared by the form on the home
// page and /api/co-develop so the two can never disagree about a key.

// Who you are. Pick as many as fit: plenty of barbers are also creators, and
// plenty of founders are also engineers.
export const CO_DEVELOP_ROLES = [
  "barber",
  "owner",
  "stylist",
  "entrepreneur",
  "designer",
  "engineer",
  "tech",
  "artist",
  "creator",
  "other",
] as const
export type CoDevelopRole = (typeof CO_DEVELOP_ROLES)[number]

export const CO_DEVELOP_INTERESTS = ["feedback", "testing", "design", "engineering", "marketing", "shop"] as const
export type CoDevelopInterest = (typeof CO_DEVELOP_INTERESTS)[number]

/** Shop name only means something to someone who works in a shop. */
export const rolesHaveShop = (roles: readonly CoDevelopRole[]) =>
  roles.some((r) => r === "barber" || r === "owner" || r === "stylist")

export interface CoDevelopApplication {
  roles: CoDevelopRole[]
  name: string
  email: string
  city: string | null
  shopName: string | null
  links: string | null
  interests: CoDevelopInterest[]
  message: string | null
}

const clip = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : "")
const orNull = (s: string) => (s ? s : null)

/**
 * Turns a request body into an application, or says what is missing. Unknown
 * roles and interests are dropped rather than trusted; a shop name from
 * someone who doesn't work in a shop is dropped too.
 */
export function parseCoDevelopApplication(
  body: Record<string, unknown>,
): { ok: true; application: CoDevelopApplication } | { ok: false; error: "missing" } {
  const roles = Array.isArray(body.roles) ? CO_DEVELOP_ROLES.filter((r) => (body.roles as unknown[]).includes(r)) : []
  const name = clip(body.name, 120)
  const email = clip(body.email, 200)
  if (!roles.length || !name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { ok: false, error: "missing" }

  const interests = Array.isArray(body.interests)
    ? CO_DEVELOP_INTERESTS.filter((i) => (body.interests as unknown[]).includes(i))
    : []

  return {
    ok: true,
    application: {
      roles,
      name,
      email,
      city: orNull(clip(body.city, 120)),
      shopName: rolesHaveShop(roles) ? orNull(clip(body.shopName, 160)) : null,
      links: orNull(clip(body.links, 500)),
      interests,
      message: orNull(clip(body.message, 2000)),
    },
  }
}
